import * as THREE from 'three';

/**
 * HDR bloom without the three.js examples: the scene renders into a half-float target
 * (so fires, lamps and the sun on water can be brighter than white), the bright parts are
 * blurred down a small mip chain (dual-filter blur), and one composite pass adds the glow back
 * and applies the game's tone mapping and colour grade.
 *
 * Cost: one extra full-screen pass plus a few tiny ones. Off on the low preset.
 */
const VERT = `varying vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`;

export class Bloom {
  enabled = true;
  strength = 0.6;
  threshold = 0.85;
  private scene: THREE.WebGLRenderTarget;
  private mips: THREE.WebGLRenderTarget[] = [];
  private quad: THREE.Mesh;
  private cam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private fsScene = new THREE.Scene();
  private bright: THREE.ShaderMaterial;
  private down: THREE.ShaderMaterial;
  private up: THREE.ShaderMaterial;
  private comp: THREE.ShaderMaterial;
  private w = 1; private h = 1;

  constructor(private renderer: THREE.WebGLRenderer, private levels = 5, samples = 4) {
    const opts = { type: THREE.HalfFloatType, depthBuffer: true, samples };
    this.scene = new THREE.WebGLRenderTarget(1, 1, opts);
    for (let i = 0; i < levels; i++) this.mips.push(new THREE.WebGLRenderTarget(1, 1, { type: THREE.HalfFloatType, depthBuffer: false }));
    this.bright = new THREE.ShaderMaterial({
      uniforms: { tMap: { value: null }, uThreshold: { value: this.threshold }, uTexel: { value: new THREE.Vector2() } }, vertexShader: VERT, toneMapped: false, depthTest: false, depthWrite: false,
      fragmentShader: `uniform sampler2D tMap; uniform float uThreshold; uniform vec2 uTexel; varying vec2 vUv;
        vec3 pick(vec2 o) { vec3 c = texture2D(tMap, vUv + o * uTexel).rgb; float l = max(c.r, max(c.g, c.b)); float k = smoothstep(uThreshold, uThreshold + 0.6, l); return c * k; }
        void main() { vec3 c = pick(vec2(-1.0, -1.0)) + pick(vec2(1.0, -1.0)) + pick(vec2(-1.0, 1.0)) + pick(vec2(1.0, 1.0)); gl_FragColor = vec4(min(c * 0.25, vec3(24.0)), 1.0); }`,
    });
    this.down = new THREE.ShaderMaterial({
      uniforms: { tMap: { value: null }, uTexel: { value: new THREE.Vector2() } }, vertexShader: VERT, toneMapped: false, depthTest: false, depthWrite: false,
      fragmentShader: `uniform sampler2D tMap; uniform vec2 uTexel; varying vec2 vUv;
        void main() { vec2 o = uTexel; vec3 c = texture2D(tMap, vUv).rgb * 4.0 + texture2D(tMap, vUv + vec2(-o.x, -o.y)).rgb + texture2D(tMap, vUv + vec2(o.x, -o.y)).rgb + texture2D(tMap, vUv + vec2(-o.x, o.y)).rgb + texture2D(tMap, vUv + vec2(o.x, o.y)).rgb; gl_FragColor = vec4(c / 8.0, 1.0); }`,
    });
    this.up = new THREE.ShaderMaterial({
      uniforms: { tMap: { value: null }, uTexel: { value: new THREE.Vector2() } }, vertexShader: VERT, toneMapped: false, depthTest: false, depthWrite: false,
      blending: THREE.AdditiveBlending, transparent: true,
      fragmentShader: `uniform sampler2D tMap; uniform vec2 uTexel; varying vec2 vUv;
        void main() { vec2 o = uTexel * 0.5; vec3 c = texture2D(tMap, vUv + vec2(-o.x * 2.0, 0.0)).rgb + texture2D(tMap, vUv + vec2(-o.x, o.y)).rgb * 2.0 + texture2D(tMap, vUv + vec2(0.0, o.y * 2.0)).rgb + texture2D(tMap, vUv + vec2(o.x, o.y)).rgb * 2.0
          + texture2D(tMap, vUv + vec2(o.x * 2.0, 0.0)).rgb + texture2D(tMap, vUv + vec2(o.x, -o.y)).rgb * 2.0 + texture2D(tMap, vUv + vec2(0.0, -o.y * 2.0)).rgb + texture2D(tMap, vUv + vec2(-o.x, -o.y)).rgb * 2.0; gl_FragColor = vec4(c / 12.0, 1.0); }`,
    });
    this.comp = new THREE.ShaderMaterial({
      uniforms: { tScene: { value: null }, tBloom: { value: null }, uStrength: { value: this.strength } }, vertexShader: VERT, toneMapped: true, depthTest: false, depthWrite: false,
      fragmentShader: `uniform sampler2D tScene; uniform sampler2D tBloom; uniform float uStrength; varying vec2 vUv;
        void main() { vec3 c = texture2D(tScene, vUv).rgb + texture2D(tBloom, vUv).rgb * uStrength; gl_FragColor = vec4(c, 1.0);
          #include <tonemapping_fragment>
          #include <colorspace_fragment>
        }`,
    });
    this.quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.comp);
    this.quad.frustumCulled = false;
    this.fsScene.add(this.quad);
  }

  setSize(w: number, h: number) {
    this.w = Math.max(1, Math.floor(w)); this.h = Math.max(1, Math.floor(h));
    this.scene.setSize(this.w, this.h);
    let mw = this.w >> 1, mh = this.h >> 1;
    for (const m of this.mips) { m.setSize(Math.max(1, mw), Math.max(1, mh)); mw >>= 1; mh >>= 1; }
  }

  private pass(mat: THREE.ShaderMaterial, target: THREE.WebGLRenderTarget | null) {
    this.quad.material = mat;
    this.renderer.setRenderTarget(target);
    this.renderer.render(this.fsScene, this.cam);
  }

  /** Compile a scene's shaders for the target it's drawn into (programs depend on it). */
  compile(scene: THREE.Scene, camera: THREE.Camera) {
    this.renderer.setRenderTarget(this.enabled ? this.scene : null);
    this.renderer.compile(scene, camera);
    this.renderer.setRenderTarget(null);
  }
  render(scene: THREE.Scene, camera: THREE.Camera) {
    const r = this.renderer;
    if (!this.enabled) { r.setRenderTarget(null); r.render(scene, camera); return; }
    const auto = r.autoClear;
    r.setRenderTarget(this.scene); r.render(scene, camera);
    // bright pass into the first mip, then blur down and back up
    this.bright.uniforms.tMap.value = this.scene.texture;
    this.bright.uniforms.uThreshold.value = this.threshold;
    this.bright.uniforms.uTexel.value.set(1 / this.w, 1 / this.h);
    this.pass(this.bright, this.mips[0]);
    for (let i = 1; i < this.mips.length; i++) {
      const src = this.mips[i - 1];
      this.down.uniforms.tMap.value = src.texture; this.down.uniforms.uTexel.value.set(1 / src.width, 1 / src.height);
      this.pass(this.down, this.mips[i]);
    }
    r.autoClear = false;
    for (let i = this.mips.length - 1; i > 0; i--) {
      const src = this.mips[i];
      this.up.uniforms.tMap.value = src.texture; this.up.uniforms.uTexel.value.set(1 / src.width, 1 / src.height);
      this.pass(this.up, this.mips[i - 1]);
    }
    r.autoClear = auto;
    this.comp.uniforms.tScene.value = this.scene.texture;
    this.comp.uniforms.tBloom.value = this.mips[0].texture;
    this.comp.uniforms.uStrength.value = this.strength;
    this.pass(this.comp, null);
  }
}
